import {
    doc, getDoc, setDoc, updateDoc,
    collection, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { SessionState } from '../types';

// ── Tipos Firestore ──────────────────────────────────────────────────────────

export interface FirestoreSession {
    id: string;
    name: string;
    hostUid: string;
    participantToken: string;
    createdAt: Timestamp | null;
    status: 'active' | 'closed';
    // El estado del lienzo se serializa como subcampos planos
    nodes: Record<string, any>;
    processes: any[];
    edges: any[];
    people: any[];
    viewBox: { x: number; y: number; w: number; h: number };
    cursors: Record<string, any>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function randomToken(len = 8): string {
    return Math.random().toString(36).slice(2, 2 + len).toUpperCase();
}


function stripUndefinedDeep<T>(value: T): T {
    if (Array.isArray(value)) {
        return value
            .map(item => stripUndefinedDeep(item))
            .filter(item => item !== undefined) as unknown as T;
    }

    if (value && typeof value === 'object') {
        const out: Record<string, unknown> = {};
        Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
            if (val === undefined) return;
            out[key] = stripUndefinedDeep(val);
        });
        return out as T;
    }

    return value;
}

// ── Crear sesión (solo el host) ───────────────────────────────────────────────

export async function createSession(
    name: string,
    hostUid: string,
    initialState: SessionState,
): Promise<string> {
    const ref = doc(collection(db, 'sessions'));
    const token = randomToken();

    await setDoc(ref, stripUndefinedDeep({
        id: ref.id,
        name,
        hostUid,
        participantToken: token,
        createdAt: serverTimestamp(),
        status: 'active',
        nodes: initialState.layout.nodes.reduce((acc, n) => ({ ...acc, [n.id]: n }), {}),
        processes: initialState.processes,
        edges: initialState.edges,
        people: initialState.people,
        viewBox: initialState.layout.viewBox,
        cursors: {},
    } as Omit<FirestoreSession, 'createdAt'> & { createdAt: any }));

    return ref.id;
}

// ── Cargar sesión ─────────────────────────────────────────────────────────────

export async function loadSession(sessionId: string): Promise<FirestoreSession | null> {
    const snap = await getDoc(doc(db, 'sessions', sessionId));
    if (!snap.exists()) return null;
    return snap.data() as FirestoreSession;
}

// ── Escribir estado completo del lienzo (debounced desde sync.ts) ─────────────

export async function writeCanvasState(
    sessionId: string,
    state: SessionState,
): Promise<void> {
    await updateDoc(doc(db, 'sessions', sessionId), stripUndefinedDeep({
        nodes: state.layout.nodes.reduce((acc, n) => ({ ...acc, [n.id]: n }), {}),
        processes: state.processes,
        edges: state.edges,
        people: state.people,
    }));
}

// ── Cerrar sesión (solo el host) ──────────────────────────────────────────────

export async function closeSession(sessionId: string): Promise<void> {
    await updateDoc(doc(db, 'sessions', sessionId), { status: 'closed' });
}

// ── Actualizar cursor de un participante ──────────────────────────────────────

export async function writeCursor(
    sessionId: string,
    cursorId: string,
    uid: string,
    x: number,
    y: number,
    displayName: string,
    color: string,
): Promise<void> {
    await updateDoc(doc(db, 'sessions', sessionId), {
        [`cursors.${cursorId}`]: { cursorId, uid, displayName, x, y, color, updatedAt: Date.now() },
    });
}

// ── Eliminar cursor al salir ──────────────────────────────────────────────────

export async function removeCursor(sessionId: string, cursorId: string): Promise<void> {
    const { deleteField } = await import('firebase/firestore');
    await updateDoc(doc(db, 'sessions', sessionId), {
        [`cursors.${cursorId}`]: deleteField(),
    });
}
